import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";
import { isAxiosError } from "axios";

import { generateMagicLink } from "@av/apps/common-apps/platform/services/authentication/authentication";
import { App } from "@av/components/atoms/app/App";
import { Button } from "@av/components/atoms/Button";
import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Typography } from "@av/components/atoms/typography/Typography";
import { AvantiLogo } from "@av/components/molecules/logo/AvantiLogo";
import { getRetryAfterMinutes } from "@av/pages/auth/utils/retry-after";
import { useReturnTo } from "@av/pages/auth/utils/return-to";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { useRevalidateOnLanguageChange } from "@av/utils/hooks/useRevalidateOnLanguageChange";
import { MagicLinkSuccess } from "./components/MagicLinkSuccess";

const useStyles = createStyles(({ token, css }) => ({
  formWrapper: css`
    width: 100%;
    max-width: 420px;
    padding: ${token.paddingMD}px;

    @media (max-width: ${token.screenMD}px) {
      max-width: 350px;
      padding: 0;
    }
  `,
  validationMessage: css`
    display: block;
    padding-top: ${token.marginXXS}px;
  `,
}));

function extractMagicLinkExpiry(response: unknown): number | undefined {
  // Explicit failure from an older BFF that still returns a boolean.
  if (response === false) {
    throw new Error("Magic link generation failed");
  }

  // `true` from an older BFF, "" from an API predating GenerateOttResponse, or any
  // other envelope we do not recognise: the send succeeded, there is just no expiry
  // to show, which magicLinkSent_descriptionWithoutExpiry already covers.
  if (typeof response !== "object" || response === null) {
    return undefined;
  }

  const expiresIn = (response as { expiresIn?: unknown }).expiresIn;

  if (expiresIn === undefined) {
    return undefined;
  }

  if (typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0) {
    return expiresIn;
  }

  throw new Error("Magic link generation returned an invalid expiry");
}

export function MagicLink() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const { styles, theme } = useStyles();
  const { locale } = useLocaleStore();
  const returnTo = useReturnTo();
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [email, setEmail] = useState("");
  const [expiresIn, setExpiresIn] = useState<number>();
  const [form] = Form.useForm();

  useRevalidateOnLanguageChange(form);

  const onFinish = async (values: { email: string }) => {
    try {
      setLoading(true);
      const expiresIn = extractMagicLinkExpiry(
        await generateMagicLink(values.email, locale, returnTo)
      );

      setExpiresIn(expiresIn);
      setEmail(values.email);
      setLinkSent(true);
      message.success(t("auth.login.magicLinkForm_success"));
    } catch (error) {
      console.error("Magic link generation failed", error);

      // 429 is returned by the edge WAF rate limit (infra/cdk/lib/waf-stack.ts), not by the send flow.
      if (isAxiosError(error) && error.status === 429) {
        message.error(
          <span data-testid="magic-link-too-many-attempts-message">
            {t("auth.login.magicLinkForm_tooManyAttempts", {
              count: getRetryAfterMinutes(error),
            })}
          </span>
        );
      } else {
        message.error(t("auth.login.magicLinkForm_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setLinkSent(false);
    setEmail("");
    // The form instance lives here, so it outlives the form the success screen replaced.
    form.resetFields();
  };

  if (linkSent) {
    return <MagicLinkSuccess email={email} expiresIn={expiresIn} onBack={handleBack} />;
  }

  return (
    <Flex vertical align="center" justify="center" style={{ height: "100%", width: "100%" }}>
      <AvantiLogo testIdPrefix="magic-link" />
      <div className={styles.formWrapper}>
        <Button
          testIdPrefix="magic-link-form-back"
          type="link"
          style={{ padding: 0 }}
          disabled={loading}
          // Back to sign-in, not history: a bookmarked or pasted link has no earlier app page.
          onClick={() => navigate({ pathname: "/login", search: location.search })}
        >
          <ArrowLeftOutlined aria-hidden />
          {t("auth.login.magicLinkSent_back")}
        </Button>

        <Typography.Title testIdPrefix="magic-link-generate-provide-email" level={5}>
          {t("auth.login.magicLinkForm_instruction")}
        </Typography.Title>

        <Form
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          layout="vertical"
          form={form}
          name="magic_link_form"
          onFinish={onFinish}
        >
          <Form.Item
            testIdPrefix="email"
            name="email"
            style={{ marginTop: theme.marginSM }}
            labelString={t("auth.login.magicLinkForm_email")}
            rules={[
              {
                required: true,
                message: (
                  <span
                    data-testid="magic-link-email-required-error"
                    className={styles.validationMessage}
                  >
                    {t("auth.login.magicLinkForm_missingEmail")}
                  </span>
                ),
              },
              {
                type: "email",
                message: (
                  <span
                    data-testid="magic-link-email-invalid-error"
                    className={styles.validationMessage}
                  >
                    {t("auth.login.magicLinkForm_invalidEmail")}
                  </span>
                ),
              },
            ]}
            normalize={(value) => value?.trim()}
          >
            <Input
              placeholder={t("auth.login.magicLinkForm_email")}
              testIdPrefix="email-input"
              size="large"
              type="email"
            />
          </Form.Item>

          <Form.Item
            testIdPrefix="send-link-button"
            // Ant Design swaps the email field's bottom margin for its error message, so the gap
            // above the button lives here. Padding, because a margin would collapse into it.
            style={{ paddingTop: theme.marginSM }}
            labelString={t("auth.login.magicLinkForm_sendLink")}
          >
            <Button
              testIdPrefix="send-link"
              type="primary"
              htmlType="submit"
              loading={loading}
              block
            >
              {t("auth.login.magicLinkForm_sendLink")}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Flex>
  );
}
